/**
 * Applies RTK Query cache tags to the generated event endpoints.
 * This file is intentionally separate from the generated engineApi.ts so that
 * running `npm run codegen` never overwrites the tag configuration.
 *
 * enhanceEndpoints mutates the api in-place and returns the same reference,
 * so the hooks exported from engineApi.ts automatically get the tags.
 * We do NOT re-export the hooks here to avoid duplicate named export errors.
 */
import { engineApi } from './engineApi'

// ── Domain types not yet generated from the OpenAPI spec ─────────────────────

/** A file attached to a participant's presentation slot. */
export interface PresentationSummary {
  status?: string
  fileName?: string
  fileSize?: number
  uploadedAt?: string
  reminderSentAt?: string
  acceptedAt?: string
  rejectedAt?: string
  rejectionMessage?: string
  uploadedByOrganizer?: boolean
}

/** One entry in the listParticipants response. */
export interface ParticipantSummary {
  id?: string
  email?: string
  firstName?: string
  lastName?: string
  role?: string
  status?: string
  acceptedAt?: string
  lastInvitedAt?: string
  registeredAt?: string
  attendedAt?: string
  customMessage?: string
  companyName?: string
  consentDataCollection?: boolean
  consentMediaRecording?: boolean
  registeredByOrganizer?: boolean
  rejectedAt?: string
  rejectionMessage?: string
  lastParticipationReminderSentAt?: string
  presentation?: PresentationSummary
  /** First-generation timestamp for the participant's Certificate of Attendance (EP-13). */
  certificateGeneratedAt?: string
  /** Most recent CoA email send/re-send timestamp; drives the organiser table's clickable cell. */
  certificateLastSentAt?: string
  /** Most recent event-media notification send/re-send timestamp (EP-15); drives the media cell. */
  mediaLastSentAt?: string
  /** Free-text note the organiser wrote for this participant, shown on their ticket. */
  organizerNote?: string | null
}

/** Typed shape of the listParticipants API response body. */
export interface ListParticipantsResponse {
  participants: ParticipantSummary[]
  total?: number
}

/** Typed shape of the getEventStats API response body. */
export interface GetEventStatsResponse {
  stats: {
    invitedCount: number
    registeredCount: number
    acceptedCount: number
    attendedCount: number
  }
}

/** Extended invite body accepted by the backend (superset of generated InviteBody). */
export interface ExtendedInviteBody {
  email?: string
  firstName?: string
  lastName?: string
  role?: string
  customMessage?: string
  language?: string
}

/**
 * One entry in a participant's conversation history — every organiser communication and status
 * transition (invited, registered, accepted/rejected, presentation request/upload/review,
 * reminder, role change, …). See #158.
 */
export interface ActivityEntry {
  type: string
  /** English fallback summary — kept for legacy rows written before `params` existed. */
  message: string
  /**
   * Machine-readable interpolation values used to resolve a localized label for `type` (e.g.
   * `{ role: 'SPEAKER' }` for `ROLE_CHANGED`, or `{ variant: 'resent' }` to pick between two
   * phrasings of the same type). `undefined`/empty for entries written before this field existed
   * — `formatActivityMessage` falls back to matching the English `message`.
   */
  params?: Record<string, string> | null
  /** Custom message attached to the underlying action (reminder note, rejection reason, …). */
  note?: string | null
  at: string
}

/** Typed shape of the getParticipantActivity API response body (`ResponseEntity<?>` on the wire). */
export interface GetParticipantActivityResponse {
  success: boolean
  entries?: ActivityEntry[]
  errorCode?: string
  message?: string
}

/** Typed shape of the previewReminderEmail API response body (`ResponseEntity<?>` on the wire). */
export interface PreviewReminderEmailResponse {
  success: boolean
  errorCode?: string
  message?: string
  /** Rendered `event-reminder` email HTML, ready to display as-is (e.g. in a sandboxed iframe). */
  html?: string
}

/** Per-participant outcome of a {@link SendCertificatesResponse} bulk send (EP-13). */
export interface CertificateSendResult {
  participantId: string
  success: boolean
  /** `true` if the PDF was generated for the first time in this call; `false` on a re-send. */
  generated: boolean
  certificateLastSentAt?: string
  errorCode?: string
  message?: string
}

/** Typed shape of the sendCertificatesOfAttendance API response body. */
export interface SendCertificatesResponse {
  success: boolean
  results: CertificateSendResult[]
  errorCode?: string
  message?: string
}

/** Per-participant outcome of a {@link SendEventMediaNotificationResponse} bulk send (EP-15). */
export interface EventMediaNotificationSendResult {
  participantId: string
  success: boolean
  mediaLastSentAt?: string
  errorCode?: string
  message?: string
}

/** Typed shape of the sendEventMediaNotifications API response body (EP-15). */
export interface SendEventMediaNotificationResponse {
  success: boolean
  results: EventMediaNotificationSendResult[]
  errorCode?: string
  message?: string
}

/** Media kind accepted by the event-media upload API (EP-14). */
export type EventMediaType = 'PHOTO' | 'VIDEO' | 'AUDIO' | 'PDF'

/** One entry in the organizer listMedia response (EP-14). */
export interface MediaSummary {
  id: string
  eventId: string
  fileName: string
  s3Key: string
  mediaType: EventMediaType
  contentType?: string | null
  sizeBytes?: number | null
  uploadedBy: string
  uploadedAt: string
}

/** Typed shape of the admin listMedia API response body (EP-14). */
export interface ListMediaResponse {
  success: boolean
  media?: MediaSummary[]
  errorCode?: string
  message?: string
}

/**
 * Typed shape of admin/participant media download response bodies (EP-14).
 *
 * {@code presignedUrl} forces a browser download (attachment disposition); {@code previewUrl} has
 * no forced disposition and is safe to embed directly in an `<img>`/`<video>`/`<audio>`/`<iframe>`
 * for inline preview.
 */
export interface DownloadMediaResponse {
  success: boolean
  presignedUrl?: string
  previewUrl?: string
  fileName?: string
  errorCode?: string
  message?: string
}

/**
 * One entry in the participant listMedia response (EP-14) — omits s3Key and uploadedBy
 * for privacy.
 */
export interface ParticipantMediaSummary {
  id: string
  fileName: string
  mediaType: EventMediaType
  contentType?: string | null
  sizeBytes?: number | null
  uploadedAt: string
}

/** Typed shape of the participant listMedia API response body (EP-14). */
export interface ListParticipantMediaResponse {
  success: boolean
  media?: ParticipantMediaSummary[]
  errorCode?: string
  message?: string
}

/** Lazy download hook — codegen exports the query hook but not the lazy variant. */
export const useLazyDownloadMediaQuery = engineApi.useLazyDownloadMediaQuery

engineApi.enhanceEndpoints({
  addTagTypes: [
    'Event',
    'EventParticipants',
    'EventStats',
    'EventParticipantActivity',
    'EventMedia',
    'ScannerBundle',
  ],
  endpoints: {
    // ── Event CRUD ─────────────────────────────────────────────────────────
    listEvents: { providesTags: ['Event'] },
    getEvent: { providesTags: ['Event'] },
    createEvent: { invalidatesTags: ['Event'] },
    updateEvent: { invalidatesTags: ['Event'] },
    deleteEvent: { invalidatesTags: ['Event', 'EventParticipants', 'EventStats'] },

    // ── Participant queries ─────────────────────────────────────────────────
    listParticipants: {
      providesTags: (_result, _error, arg) => [{ type: 'EventParticipants', id: arg.eventId }],
    },
    getEventStats: {
      providesTags: (_result, _error, arg) => [{ type: 'EventStats', id: arg.eventId }],
    },
    // Conversation history modal — see #158. Tagged per-participant so an action taken from
    // inside the modal (e.g. accept/reject) invalidates just that participant's history.
    getParticipantActivity: {
      providesTags: (_result, _error, arg) => [
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },

    // ── Participant mutations — all invalidate participants + stats ──────────
    inviteParticipant: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EventParticipants', id: arg.eventId },
        { type: 'EventStats', id: arg.eventId },
      ],
    },
    batchUpdateParticipants: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EventParticipants', id: arg.eventId },
        { type: 'EventStats', id: arg.eventId },
        // MARK_ATTENDED / UNMARK_ATTENDED flip attendance, so the kiosk bundle must refetch
        // too — otherwise a just-unmarked participant still scans as ATTENDED.
        { type: 'ScannerBundle', id: arg.eventId },
        // Batch actions include per-participant asks/accepts/rejects/reminders — invalidate
        // every open conversation-history modal rather than trying to enumerate participant IDs.
        'EventParticipantActivity',
      ],
    },
    adminRegisterParticipant: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EventParticipants', id: arg.eventId },
        { type: 'EventStats', id: arg.eventId },
      ],
    },
    acceptRegistration: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        'EventStats',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    rejectRegistration: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        'EventStats',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    reopenRegistration: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        'EventStats',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    removeParticipant: {
      invalidatesTags: ['EventParticipants', 'EventStats'],
    },
    resendEventParticipantInvitation: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    assignRole: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    setParticipantNote: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    adminUploadPresentation: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    askForPresentation: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    acceptPresentation: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    rejectPresentation: {
      invalidatesTags: (_result, _error, arg) => [
        'EventParticipants',
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    // Scanner bundle (check-in kiosk) — tagged per-event so attendance mutations refetch it.
    // Without this, a kiosk that already loaded the bundle keeps serving a stale ATTENDED
    // status for a participant an organiser just set back to not-attended, and the next scan
    // is swallowed as "already attended" without ever calling recordAttendance.
    getScannerBundleAdmin: {
      providesTags: (_result, _error, arg) => [{ type: 'ScannerBundle', id: arg.eventId }],
    },
    recordAttendanceAdmin: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EventParticipants', id: arg.eventId },
        { type: 'EventStats', id: arg.eventId },
        { type: 'ScannerBundle', id: arg.eventId },
        // The backend appends an ATTENDANCE_MARKED (checkin) entry to the participant's
        // conversation history — refresh it if the modal is open.
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    clearAttendanceAdmin: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EventParticipants', id: arg.eventId },
        { type: 'EventStats', id: arg.eventId },
        { type: 'ScannerBundle', id: arg.eventId },
        // The backend appends an ATTENDANCE_CLEARED entry to the participant's
        // conversation history — refresh it if the modal is open.
        { type: 'EventParticipantActivity', id: arg.participantId },
      ],
    },
    // Certificate of Attendance (EP-13) — re-fetch the participant list so the organiser
    // table's "CoA last sent" cell picks up the new certificateGeneratedAt/certificateLastSentAt.
    sendCertificatesOfAttendance: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'EventParticipants', id: arg.eventId }],
    },
    // Event media notification (EP-15) — re-fetch participants so the "Media notification last
    // sent" cell picks up the new mediaLastSentAt.
    sendEventMediaNotifications: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'EventParticipants', id: arg.eventId }],
    },

    // ── Event media (EP-14) ───────────────────────────────────────────────────
    listMedia: {
      providesTags: (_result, _error, arg) => [{ type: 'EventMedia', id: arg.eventId }],
    },
    uploadMedia: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'EventMedia', id: arg.eventId }],
    },
    deleteMedia: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'EventMedia', id: arg.eventId }],
    },
  },
})
